import { Space } from "antd";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { BiPlus, BiTrash } from "react-icons/bi";
import Button from "shared/components/Button";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import graphql from "utils/api/graphql";
import { DATA_BINDING_MODES } from "utils/constants";

const FilterList = ({ value, onChange, labels }) => {
  const rows = value || [];
  const update = (index, patch) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  const remove = (index) => onChange(rows.filter((_, i) => i !== index));
  const add = () => onChange([...rows, { field: "", value: "" }]);

  return (
    <Space direction="vertical" size={6} className="canvas-editor__full-width">
      {rows.map((row, index) => (
        <div key={index} className="canvas-editor__list-row">
          <Input
            placeholder={labels.FILTER_FIELD}
            value={get(row, "field", "")}
            onChange={(e) => update(index, { field: e.target.value })}
          />
          <Input
            placeholder={labels.FILTER_VALUE}
            value={get(row, "value", "")}
            onChange={(e) => update(index, { value: e.target.value })}
          />
          <div className="delete-icon" onClick={() => remove(index)}>
            <BiTrash color={COLORS.C_DANGER} size={16} />
          </div>
        </div>
      ))}
      <Button type="primary--outlined" onClick={add}>
        <BiPlus size={16} /> {labels.ADD_FILTER}
      </Button>
    </Space>
  );
};

const DataBindingListField = ({ value, onChange, labels }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const DATA = t("FORM.STATUSES.DATA", { returnObjects: true });
  const modeOptions = [
    { value: DATA_BINDING_MODES.ONE, label: DATA.MODE_ONE },
    { value: DATA_BINDING_MODES.MANY, label: DATA.MODE_MANY },
  ];

  const { data } = useQuery(graphql.queries.DATA_ENTITIES);
  const entities = get(data, "dataEntities") || [];
  const tokens = get(data, "dataBindingTokens") || [];
  const entityOptions = entities.map((entity) => ({
    value: entity.key,
    label: `${entity.label} (${entity.key})`,
  }));

  const items = value || [];
  const update = (index, patch) =>
    onChange(
      items.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    );
  const remove = (index) => onChange(items.filter((_, i) => i !== index));
  const add = () =>
    onChange([
      ...items,
      {
        key: "",
        entity: null,
        mode: DATA_BINDING_MODES.ONE,
        filters: [],
      },
    ]);

  const pickEntity = (index, entityKey) => {
    const entity = entities.find((candidate) => candidate.key === entityKey);
    update(index, {
      entity: entityKey,
      mode: get(entity, "defaultMode") || DATA_BINDING_MODES.ONE,
      filters: get(entity, "defaultFilters") || [],
    });
  };

  return (
    <Space direction="vertical" size={10} className="canvas-editor__full-width">
      {items.map((item, index) => (
        <div key={index} className="canvas-editor__card">
          <div className="canvas-editor__row-end">
            <div className="delete-icon" onClick={() => remove(index)}>
              <BiTrash color={COLORS.C_DANGER} size={18} />
            </div>
          </div>
          <Input
            label={labels.KEY}
            placeholder={labels.KEY}
            value={item.key || ""}
            onChange={(e) => update(index, { key: e.target.value })}
          />
          <div className="canvas-editor__field-block">
            <Select
              showSearch
              label={labels.ENTITY}
              placeholder={labels.ENTITY}
              options={entityOptions}
              value={item.entity || undefined}
              onChange={(entityKey) => pickEntity(index, entityKey)}
            />
          </div>
          <div className="canvas-editor__field-block">
            <Select
              label={labels.MODE}
              placeholder={labels.MODE}
              options={modeOptions}
              value={item.mode || DATA_BINDING_MODES.ONE}
              onChange={(mode) => update(index, { mode })}
            />
          </div>
          <div className="canvas-editor__card-title">{labels.FILTERS}</div>
          <FilterList
            value={item.filters}
            onChange={(filters) => update(index, { filters })}
            labels={labels}
          />
        </div>
      ))}
      {tokens.length ? (
        <div className="canvas-editor__meta-xs">
          {labels.TOKENS}: {tokens.map((token) => `{{${token}}}`).join(" · ")}
        </div>
      ) : null}
      <Button type="primary--outlined" onClick={add}>
        <BiPlus size={18} /> {labels.ADD}
      </Button>
    </Space>
  );
};

export default DataBindingListField;
